import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  ListRow,
  ListRowGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAccountChip,
  SodActVsPotChip,
  SodColumnChip,
  SodEndPointChip,
  SodListLoader,
  SodMessage,
  SodReloader,
  SodRiskPriorityChip,
  SodUserChip,
} from '../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_BLD,
  RENDER_MODES,
  SEARCH_TEXT_LENGTH,
  TABS_FILTERS,
  testIds,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useInViewObserver,
  useTypedSelector,
} from '../../../../shared/hooks';
import {
  type ListTrackerState,
  type TrackedListRowState,
} from '../../../../shared/types';
import { createDevRejectionHandler } from '../../../../shared/utils';
import { detailsActions } from '../../../Details/data-store/detailsSlice';
import {
  useGetViolationsGraphCusterMutation,
  useGetViolationsListMutation,
} from '../../data-layer/violationsTabApi';
import { type ViolationsTabSidePanelGroupRowContextState } from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import {
  configureTracker,
  findKeyValue,
  getListPayload,
  getRowContext,
} from '../../utils';
import { ViolationsListActions } from '../ViolationsListActions';

import './ViolationsTabList.css';

const MAX_COUNT = 50;
const LEAVE_ANIM_DURATION = 600;

/**
 * ViolationsTabList
 * @description A component that renders a list of violations in a tabbed interface.
 * This component is responsible for displaying and managing violations data within
 * the violations tab section of the application.
 *
 * @typedef {Object} Violation
 * @property {string} id - Unique identifier for the violation
 * @property {string} title - Title or summary of the violation
 * @property {string} description - Detailed description of the violation
 * @property {string} severity - Severity level of the violation
 * @property {Date} timestamp - When the violation occurred
 *
 * @typedef {Object} ViolationsTabListProps
 * @property {Violation[]} violations - Array of violation objects to be displayed
 * @property {boolean} isLoading - Flag indicating if violations are being loaded
 * @property {(id: string) => void} onViolationSelect - Callback function when a violation is selected
 * @property {string} [selectedViolationId] - ID of the currently selected violation
 * @property {Object} [filters] - Optional filters to apply to the violations list
 * @property {'asc' | 'desc'} [sortOrder='desc'] - Sort order for the violations list
 *
 * @example
 * ```tsx
 * <ViolationsTabList
 *   violations={violationsData}
 *   isLoading={false}
 *   onViolationSelect={(id) => handleViolationSelect(id)}
 *   selectedViolationId="violation-123"
 *   sortOrder="desc"
 * />
 * ```
 *
 * @returns {JSX.Element} A rendered list of violations in a tabbed interface
 *
 * @accessibility
 * - Uses semantic HTML elements for better screen reader navigation
 * - Implements ARIA attributes for improved accessibility
 * - Supports keyboard navigation through the violations list
 * - Maintains sufficient color contrast for all text elements
 *
 * @performance
 * - Implements virtualization for large lists
 * - Uses memoization to prevent unnecessary re-renders
 * - Optimizes data filtering and sorting operations
 */
function ViolationsTabList(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    violationsTab: {
      controls: {
        renderMode,
        tabInAction,
        activeFilters: mainActiveFilters,
        searchKey,
        sortInAction,
        activeColumns,
        activeInsight,
        activeGraphPoint,
        activeWidgetItem,
      },
      list: { totalCount, rowIdToTransition, isRefresh: isListRefresh },
      bulkaction: {
        isBulkChecked,
        isFooterVisible,
        sodKeys,
        isClear: isBulkClear,
        actionInfo,
      },
      sidepanel: { isSidePanelVisible },
    },
    global: { isGlobalLoading, isReadOnlyAction },
    details: {
      entitlementSummary: { selectedEntitlement },
      entitlementViolationsTab: { activeFilters: entitlementActiveFilters },
    },
  } = useTypedSelector((state) => state.aag);

  const isEntitlementContext = window.location.pathname.includes('entitlement');
  const activeFilters = isEntitlementContext
    ? entitlementActiveFilters
    : mainActiveFilters;

  // API endpoints
  const [fetchViolationsList, { isLoading: isListLoading }] =
    useGetViolationsListMutation();
  const [fetchGraphCluster, { isLoading: isGraphClusterLoading }] =
    useGetViolationsGraphCusterMutation();

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedListRowState[]);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Constants
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        totalCount === null ||
          (currentOffset < totalCount && listRows.length < totalCount)
      ),
    [currentOffset, totalCount, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && totalCount !== null && !isValidApiCall),
    [totalCount, isListZero, isValidApiCall]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );
  const isCheckboxDisabled = useMemo(
    () =>
      Boolean(
        tabInAction === TABS_FILTERS.REMEDIATEDCOUNT.value ||
          tabInAction === TABS_FILTERS.CLOSEDCOUNT.value ||
          isReadOnlyAction
      ),
    [tabInAction, isReadOnlyAction]
  );

  useEffect(() => {
    dispatch(
      violationsTabActions.setIsListLoading(
        isListLoading || isGraphClusterLoading
      )
    );
  }, [dispatch, isListLoading, isGraphClusterLoading]);

  // Call through list API
  const handleListApiCall = useCallback(
    async (offset: number) => {
      abortRef.current?.('New Search');

      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map(
          (f: { value: string; options: { value: string | number }[] }) => [
            f.value,
            f.options.map((o) => String(o.value)),
          ]
        )
      );
      const payload = getListPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey,
        sortInAction,
        activeInsight,
        { max: MAX_COUNT, offset },
        activeWidgetItem !== null ? activeWidgetItem.data.filter : null
      );

      if (isEntitlementContext) {
        if (
          selectedEntitlement?.entitlementValue &&
          selectedEntitlement.riskObjects.length
        ) {
          if (
            !activeFilters.some(
              (f: { value: string }) =>
                f.value ===
                VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENT_VALUE.value
            )
          ) {
            payload.filters.entitlement = {
              ...payload.filters.entitlement,
              value: [selectedEntitlement.entitlementValue],
            };
          }

          if (
            !activeFilters.some(
              (f: { value: string }) =>
                f.value === VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value
            )
          ) {
            payload.filters.risk = {
              ...payload.filters.risk,
              riskName: selectedEntitlement.riskObjects
                .map((riskObj) => riskObj.riskName)
                .filter((risk): risk is string => Boolean(risk)),
            };
          }
        }
      }

      try {
        const controller = fetchViolationsList(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchViolationsList,
      activeFilters,
      tabInAction,
      searchKey,
      sortInAction,
      activeInsight,
      activeWidgetItem,
      isEntitlementContext,
    ]
  );

  // call through graph API
  const handleGraphApiCall = useCallback(
    async (offset: number) => {
      if (activeGraphPoint) {
        abortRef.current?.('New Search');

        // Map for the active options for different filters
        const activeFiltersMap = Object.fromEntries(
          activeFilters.map(
            (f: { value: string; options: { value: string | number }[] }) => [
              f.value,
              f.options.map((o) => String(o.value)),
            ]
          )
        );
        const payload = getListPayload(
          activeFiltersMap,
          tabInAction ?? undefined,
          searchKey,
          sortInAction,
          activeInsight,
          { max: MAX_COUNT, offset }
        );

        if (isEntitlementContext) {
          if (
            selectedEntitlement?.entitlementValue &&
            selectedEntitlement.riskObjects.length
          ) {
            if (
              !activeFilters.some(
                (f: { value: string }) =>
                  f.value ===
                  VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENT_VALUE.value
              )
            ) {
              payload.filters.entitlement = {
                ...payload.filters.entitlement,
                value: [selectedEntitlement.entitlementValue],
              };
            }

            if (
              !activeFilters.some(
                (f: { value: string }) =>
                  f.value === VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value
              )
            ) {
              payload.filters.risk = {
                ...payload.filters.risk,
                riskName: selectedEntitlement.riskObjects
                  .map((riskObj) => riskObj.riskName)
                  .filter((risk): risk is string => Boolean(risk)),
              };
            }
          }
        }

        try {
          const controller = fetchGraphCluster({
            clusterId: activeGraphPoint.clusterId,
            payload: {
              filters: payload.filters,
              pagination: payload.pagination,
            },
          });

          abortRef.current = () => controller.abort();

          const response = await controller.unwrap();

          return response;
        } catch (error) {
          if ((error as { name?: string }).name !== ABORT_ERROR) {
            setIsListError(true);
            setIsObserverInView(false);

            throw error;
          }

          throw Error(ABORT_ERROR);
        }
      }

      throw Error('Cluster ID not available');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fetchGraphCluster,
      activeGraphPoint,
      activeFilters,
      tabInAction,
      searchKey,
      sortInAction,
      activeInsight,
      isEntitlementContext,
    ]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isObserverInView || !isValidApiCall) {
        return;
      }

      Logger.log('Fetching', currentOffset, 'to', currentOffset + MAX_COUNT);

      if (activeGraphPoint) {
        handleGraphApiCall(currentOffset)
          .then((response) => {
            setIsObserverInView(false);
            setCurrentOffset((prev) => prev + MAX_COUNT);
            setIsListError(false);

            const { totalRecords, data } = response as {
              totalRecords: number;
              data: TrackedListRowState[] | null | undefined;
            };

            if (!data) {
              dispatch(violationsTabActions.setListTotalCount(0));

              return;
            }

            const newRows = configureTracker(
              data,
              isBulkChecked,
              isReadOnlyAction
            );

            setListRows((prev) => [
              ...prev.map((row) => ({
                ...row,
                tracker: {
                  ...row.tracker,
                  isFirstAddded: false,
                },
              })),
              ...newRows,
            ]);
            dispatch(violationsTabActions.setListTotalCount(totalRecords));
          })
          .catch(
            createDevRejectionHandler('ViolationsTabList: handleListApiCall')
          );

        return;
      }

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response as {
            totalRecords: number;
            data: TrackedListRowState[] | null | undefined;
          };

          if (!data) {
            dispatch(violationsTabActions.setListTotalCount(0));

            return;
          }

          const newRows = configureTracker(
            data,
            isBulkChecked,
            isReadOnlyAction
          );

          setListRows((prev) => [
            ...prev.map((row) => ({
              ...row,
              tracker: {
                ...row.tracker,
                isFirstAddded: false,
              },
            })),
            ...newRows,
          ]);
          dispatch(violationsTabActions.setListTotalCount(totalRecords));
        })
        .catch(
          createDevRejectionHandler('ViolationsTabList: handleListApiCall')
        );
    }, 10);

    return () => clearTimeout(debounceTimer);
  }, [
    dispatch,
    isObserverInView,
    isReadOnlyAction,
    isValidApiCall,
    currentOffset,
    isBulkChecked,
    activeGraphPoint,
    handleListApiCall,
    handleGraphApiCall,
    activeFilters,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        batch(() => {
          dispatch(violationsTabActions.setIsBulkChecked(false));
          dispatch(violationsTabActions.setListTotalCount(null));
          dispatch(violationsTabActions.setIsSidePanelVisible(false));
        });
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);
        setIsObserverInView(true);
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [
    // Add all the dependencies based on which you want to refresh the page
    dispatch,
    isListRefresh,
    isGlobalLoading,
    tabInAction,
    searchKey,
    sortInAction,
    activeInsight,
    activeGraphPoint,
    activeWidgetItem,
    activeFilters,
  ]);

  // Removing connectors if any when `list` mode and sidepanel closed
  useEffect(() => {
    if (renderMode === RENDER_MODES.LIST && !isSidePanelVisible) {
      setListRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        }))
      );
    }
  }, [isSidePanelVisible, renderMode]);

  // Individual checkboxes
  const handleCheckBoxChange = useCallback(
    (
      id: string,
      riskId: string,
      ruleset: string,
      riskName: string,
      allowedActions: string[]
    ) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!id) {
          return;
        }

        const targetState = Boolean(e.target.checked);

        setListRows((prevListRows) =>
          prevListRows.map((row) => {
            if (row.tracker.id === id) {
              return {
                ...row,
                tracker: {
                  ...row.tracker,
                  isChecked: targetState,
                },
              };
            }

            return row;
          })
        );

        if (targetState) {
          dispatch(
            violationsTabActions.setBulkActionInfo([
              ...actionInfo,
              { id, riskId, ruleset, riskName, allowedActions },
            ])
          );
        } else {
          dispatch(
            violationsTabActions.setBulkActionInfo(
              actionInfo.filter((m) => !(m.id === id))
            )
          );
        }

        const tempSodKeys = {
          in: [...sodKeys.in],
          notIn: [...sodKeys.notIn],
        };

        if (isBulkChecked && targetState) {
          tempSodKeys.notIn = tempSodKeys.notIn.filter((idx) => idx !== id);
        }

        if (isBulkChecked && !targetState) {
          tempSodKeys.notIn = [...tempSodKeys.notIn, id];
        }

        if (!isBulkChecked && targetState) {
          tempSodKeys.in = [...tempSodKeys.in, id];
        }

        if (!isBulkChecked && !targetState) {
          tempSodKeys.in = tempSodKeys.in.filter((idx) => idx !== id);
        }

        batch(() => {
          dispatch(violationsTabActions.setBulkSodKeys(tempSodKeys));

          if (!isBulkChecked && tempSodKeys.in.length === 0) {
            dispatch(violationsTabActions.setIsFooterVisible(false));
          }

          if (!isBulkChecked && tempSodKeys.in.length > 0) {
            dispatch(violationsTabActions.setIsFooterVisible(true));
          }

          if (isBulkChecked) {
            const count = (totalCount ?? 0) - tempSodKeys.notIn.length;

            if (count === 0) {
              dispatch(violationsTabActions.setIsBulkChecked(false));
            } else {
              dispatch(violationsTabActions.setBulkCount(count));
            }
          } else {
            dispatch(violationsTabActions.setBulkCount(tempSodKeys.in.length));
          }
        });
      },
    [dispatch, isBulkChecked, sodKeys, totalCount, actionInfo]
  );

  // Sidepanel handling and row connector lgic on `row` click
  const handleRowClick = useCallback(
    (id: string) => {
      let targetIndex: number | undefined;
      const rowContext: ViolationsTabSidePanelGroupRowContextState = {
        current: { id: '' },
      };
      const listLength = listRows.length;

      const modifiedRows = listRows.map((row, index) => {
        if (row.tracker.id === id) {
          targetIndex = index;

          return {
            ...row,
            tracker: {
              ...row.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      if (targetIndex !== undefined) {
        // Will always have the current row
        rowContext.current = getRowContext(modifiedRows[targetIndex]);

        if (targetIndex < listLength - 1 && listLength > 1) {
          rowContext.next = getRowContext(modifiedRows[targetIndex + 1]);
        }

        // First element is clicked
        if (targetIndex === 0 && listLength > 1) {
          rowContext.next = getRowContext(modifiedRows[targetIndex + 1]);
        }

        setListRows(() => modifiedRows);

        dispatch(violationsTabActions.setRowContext(rowContext));

        if (!isSidePanelVisible) {
          dispatch(violationsTabActions.setIsSidePanelVisible(true));
        }
      }
    },
    [dispatch, isSidePanelVisible, listRows]
  );

  // Handling checkbox based on the bulk checkbox
  useEffect(() => {
    if (isBulkClear) {
      setListRows((prevListRows) =>
        prevListRows.map((row) => ({
          ...row,
          tracker: { ...row.tracker, isChecked: false },
        }))
      );

      batch(() => {
        dispatch(violationsTabActions.setBulkIsClear(false));
        dispatch(violationsTabActions.setIsBulkChecked(false));
      });

      return;
    }

    setListRows((prevListRows) =>
      prevListRows.map((row) => ({
        ...row,
        tracker: { ...row.tracker, isChecked: isBulkChecked },
      }))
    );
  }, [dispatch, isBulkChecked, isBulkClear]);

  // Transition logic here
  useEffect(() => {
    if (rowIdToTransition !== null) {
      const nextItem: { tracker?: ListTrackerState } = {};

      // Start the transition
      setListRows((prevListRows) =>
        prevListRows.map((row, index) => {
          if (row.tracker.id === rowIdToTransition) {
            // Check if next available item is present
            if (
              index !== prevListRows.length - 1 &&
              row.tracker.isRowDetailsOpen
            ) {
              nextItem.tracker = prevListRows[index + 1].tracker;
            }

            return {
              ...row,
              tracker: { ...row.tracker, isTransition: true },
            };
          }

          return row;
        })
      );

      setTimeout(() => {
        if (nextItem.tracker?.id) {
          handleRowClick(nextItem.tracker.id);
        } else {
          dispatch(violationsTabActions.setIsSidePanelVisible(false));
        }
      }, 0);

      // End the transition and manage the next item with sidepanel
      setTimeout(() => {
        setListRows((prevListRows) =>
          prevListRows
            .filter((row) => row.tracker.id !== rowIdToTransition)
            .map((row) => {
              if (nextItem.tracker && row.tracker.id === nextItem.tracker.id) {
                return {
                  ...row,
                  tracker: { ...row.tracker, isRowDetailsOpen: true },
                };
              }

              return row;
            })
        );

        dispatch(violationsTabActions.setListTransitionId(null));
      }, LEAVE_ANIM_DURATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRowClick should be avoided
  }, [dispatch, rowIdToTransition]);

  // Clear filter on no data
  const handleClearBtn = useCallback(() => {
    if (isEntitlementContext) {
      // In entitlement context, only clear non-disabled filters
      const clearedFilters = activeFilters.filter((filter) =>
        filter.options.some((option) => option.isDisabled)
      );

      dispatch(
        detailsActions.setEntitlementViolationsActiveFilters(clearedFilters)
      );
    }

    dispatch(violationsTabActions.setActiveFilters([]));
  }, [activeFilters, isEntitlementContext, dispatch]);

  // Render body here
  const renderListBody = useCallback(() => {
    // No data Screens
    if (isZeroCount) {
      return (
        <SodMessage
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          handleFilterClearBtn={handleClearBtn}
          defaultTitle={l('No Open Violations')}
          defaultMessage={l('Keep up the good work!')}
        />
      );
    }

    return (
      <ListRowGroup className='Sod-ViolationsTabList--isListRowGroup'>
        {/* List rows */}
        {listRows.map((row) => {
          const {
            actualVsPotential,
            risk: { riskName, riskScore, riskSeverity, riskId, ruleset },
            user: { firstName, lastName, username },
            tracker: {
              id,
              isChecked,
              isRowDetailsOpen,
              isTransition,
              isFirstAddded,
            },
            userCapability: { allowedActions },
            accounts,
            endpoints,
          } = row;

          const rowContext = getRowContext(row);
          const accountCount = accounts.length;

          const rowClasses = cx(
            'Sod-ViolationsTabList-listRow',
            isRowDetailsOpen && 'Sod-ViolationsTabList-listRow--isConnected',
            isTransition && 'Sod-ViolationsTabList-listRow--isTransition',
            isFirstAddded && 'Sod-ViolationsTabList-listRow--isFirstAddded'
          );

          const selectedColumns = activeColumns.filter(
            (column) => column.isChecked
          );

          const listDefaultColoumns = [
            <SodRiskPriorityChip
              key='SodRiskPriorityChip'
              priority={riskSeverity}
              riskScore={riskScore}
            />,
            <SodActVsPotChip key='SodActVsPotChip' value={actualVsPotential} />,
            accountCount > 0 && (
              <SodEndPointChip
                key='SodEndPointChip'
                endpoints={endpoints.map((endpoint) => ({
                  endpointName: endpoint.endpointName,
                  endpointLogo: endpoint.endpointLogo,
                }))}
              />
            ),
            <SodUserChip
              key='SodUserChip'
              firstName={firstName}
              lastName={lastName}
              userName={username}
              isPrefixInterpoint={false}
            />,
            accountCount > 0 && (
              <SodAccountChip
                key='SodAccountChip'
                visibleAccountName={accounts[0].accountName}
                accountCount={accountCount}
                isPrefixInterpoint={Boolean(selectedColumns.length)}
              />
            ),
          ];

          const listConfigurableColumns = selectedColumns.map(
            (column, index) => {
              const { value: originalValue, label } = column as {
                value: string;
                label: string;
              };
              let value = originalValue;

              if (value === 'rulesetName') {
                value = 'ruleset';
              }

              if (value === 'userStatus') {
                value = 'status';
              }

              const columnValue = findKeyValue(row, value);

              return (
                <SodColumnChip
                  key={value}
                  name={label}
                  value={columnValue}
                  isPrefixInterpoint={index < selectedColumns.length - 1}
                />
              );
            }
          );

          const allColumnsToRender = [
            ...listDefaultColoumns,
            ...listConfigurableColumns,
          ];

          return (
            <ListRow
              key={id}
              className={rowClasses}
              dataTestId={testIds.SOD_VIOLATIONS_TAB_LIST_ROW}
              leadingComp={<ApplicationIcon logo='SOD' size='medium' />}
              isCheckboxDisabled={isCheckboxDisabled}
              centerContent={
                <Box className='Sod-ViolationsTabList-centerContent'>
                  <Typography
                    kind='h3'
                    desktop='h3-d'
                    mobile='h3-d'
                    color={CF_NEU_BLD}>
                    {riskName}
                  </Typography>
                  <Box className='Sod-ViolationsTabList-centerContentStatuses'>
                    {allColumnsToRender}
                  </Box>
                </Box>
              }
              trailingContent={
                !isSidePanelVisible && (
                  <ViolationsListActions
                    tabInAction={tabInAction}
                    isDisabled={isFooterVisible}
                    rowContext={rowContext}
                  />
                )
              }
              isChecked={isChecked}
              handleCheckBoxChange={handleCheckBoxChange(
                id,
                riskId,
                ruleset,
                riskName,
                allowedActions
              )}
              handleRowClick={() => handleRowClick(id)}
              isRowDetailsOpen={isRowDetailsOpen}
              connectorDistance='3.125rem'
              isRowConnectTransitionEnabled
            />
          );
        })}
        {/* Observer Element */}
        {!isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isListError && isValidApiCall && <SodListLoader count={3} />}
        {/* Retry mechanism */}
        {isListError && (
          <SodReloader triggerRefresh={() => setIsListError(false)} />
        )}
      </ListRowGroup>
    );
  }, [
    l,
    activeColumns,
    listObserverElement,
    isFooterVisible,
    isSidePanelVisible,
    listRows,
    tabInAction,
    isValidApiCall,
    isZeroCount,
    isCheckboxDisabled,
    isSearchActive,
    isFilterActive,
    isListError,
    handleRowClick,
    handleClearBtn,
    handleCheckBoxChange,
  ]);

  return (
    <Box
      className='Sod-ViolationsTabList'
      dataTestId={testIds.SOD_VIOLATIONS_TAB_LIST}>
      {renderListBody()}
    </Box>
  );
}

export default memo(ViolationsTabList);
